"use client";

import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { useRef, useState, type ReactNode } from "react";
import { Braces, Check, Download, Loader2, Play, Save, Settings } from "lucide-react";
import { usePathname, useRouter } from "next/navigation";
import { useTranslations } from "next-intl";
import type { PlaygroundLanguage } from "../config/languages";
import type { EditorSettings } from "../lib/editor-settings";
import type { FormatSupport } from "../lib/format-code";
import type { SnippetEmbedSettings } from "../types";
import { PlaygroundIdeWordmark } from "./PlaygroundLayout";
import { EditorSettingsPanel } from "./EditorSettingsPanel";
import { PlaygroundSnippetsQuickAccess } from "./PlaygroundSnippetsQuickAccess";
import { ShareSnippetPopover } from "./ShareSnippetPopover";
import { ToolbarOverflowMenu } from "./ToolbarOverflowMenu";
import {
  PlaygroundToolbarAccount,
  PlaygroundToolbarAccountMobileFooter,
} from "./PlaygroundToolbarAccount";
import { PlaygroundUpgradeModal } from "./PlaygroundUpgradeModal";
import { cn } from "@/lib/utils";

type PlaygroundToolbarProps = {
  locale: string;
  /** When true, show My Snippets quick access next to the language selector. */
  isAuthenticated?: boolean;
  languages: readonly PlaygroundLanguage[];
  currentLanguageId: string;
  onLanguageChange: (languageId: string) => void;
  onRun: () => void;
  isRunning: boolean;
  canRun: boolean;
  /** Format Code (`PG-IDE02`): the button is always reachable; an unsupported language explains itself on click. */
  onFormat?: () => void;
  isFormatting?: boolean;
  formatSupport?: FormatSupport;
  /** Signed-in users only — docs/24 §4.3 (anonymous: no file download). */
  onDownloadFile?: () => void;
  canDownloadSource?: boolean;
  editorSettings: EditorSettings;
  onEditorSettingsChange: (next: EditorSettings) => void;
  /** Save current snippet — no-op when disabled. */
  onSaveClick: () => void;
  onSnippetSelect: (snippetId: string, languageId: string) => void;
  savePending?: boolean;
  saveDisabled?: boolean;
  saveDisabledTooltip?: string;
  /** When set, user can generate a share link. */
  savedSnippetId: string | null;
  shareSlug: string | null;
  isPasswordProtected: boolean;
  /** The owner's embed opt-in, threaded to the share panel (`PG-EMBED01`). */
  snippetEmbed?: SnippetEmbedSettings;
  /** Language SEO pages only — show the guide-visibility toggle in settings. */
  showLanguageGuideToggle?: boolean;
  /** Runtime version + compiler flags (`PG-IDE01`) — rendered in both settings surfaces. */
  runtimePanel?: ReactNode;
  /**
   * Version history for a saved snippet (`PG-IDE05`).
   *
   * A node rather than a prop bundle, the same way `runtimePanel` is: the panel
   * needs the plan, the buffer and the save path, and the toolbar needs none of
   * them. It sits beside quick access, which is visible at every breakpoint, so
   * mobile reaches it without a second copy in the overflow menu.
   */
  historyPanel?: ReactNode;
  /**
   * `PG-PWA01`. `status` is the connectivity chip, rendered as its own row so
   * it fits every breakpoint; `offline` disables share with its reason (Run
   * and Save arrive disabled with theirs); `runDisabledReason` is the Run
   * button's title while it is off; `onInstall` is present only while the
   * browser holds an install prompt.
   */
  status?: ReactNode;
  offline?: boolean;
  runDisabledReason?: string;
  onInstall?: () => void;
};

const secondaryBtn =
  "inline-flex shrink-0 items-center justify-center gap-2 rounded-lg border border-white/15 bg-white/5 text-sm font-medium text-white/90 transition hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#E94D1D]/45 focus-visible:ring-offset-2 focus-visible:ring-offset-[#0B0B0C] disabled:cursor-not-allowed disabled:opacity-40";

const secondaryIconSize = "h-11 min-h-[44px] min-w-[44px] px-0 md:h-10 md:min-h-0 md:min-w-0 md:px-2.5 xl:px-3";

export function PlaygroundToolbar({
  locale,
  isAuthenticated = false,
  languages,
  currentLanguageId,
  onLanguageChange,
  onRun,
  isRunning,
  canRun,
  onFormat,
  isFormatting = false,
  formatSupport = "unsupported",
  onDownloadFile,
  canDownloadSource = true,
  editorSettings,
  onEditorSettingsChange,
  onSaveClick,
  onSnippetSelect,
  savePending = false,
  saveDisabled = false,
  saveDisabledTooltip,
  savedSnippetId,
  shareSlug,
  isPasswordProtected,
  snippetEmbed,
  showLanguageGuideToggle = false,
  runtimePanel,
  historyPanel,
  status,
  offline = false,
  runDisabledReason,
  onInstall,
}: PlaygroundToolbarProps) {
  const t = useTranslations("playground");
  const pathname = usePathname();
  const router = useRouter();
  const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);
  /** Hover-prefetch each language page at most once per mount. */
  const prefetchedSlugsRef = useRef<Set<string>>(new Set());
  const current = languages.find((l) => l.id === currentLanguageId) ?? languages[0];

  const nextParam = pathname || `/${locale}/playground`;

  const languageSelect = (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button
          type="button"
          title={t("selectLanguage")}
          aria-label={t("selectLanguage")}
          className="inline-flex h-11 max-w-[min(100%,200px)] min-h-[44px] items-center justify-between gap-1.5 rounded-lg border border-white/15 bg-white/5 px-2.5 text-left text-sm text-white transition hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#E94D1D]/45 focus-visible:ring-offset-2 focus-visible:ring-offset-[#0B0B0C] md:h-10 md:min-h-0 md:max-w-[min(100%,220px)] md:gap-2 md:px-3"
        >
          <span className="truncate">{current?.name ?? currentLanguageId}</span>
          <span className="text-white/50">▾</span>
        </button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          className="z-[200] max-h-[min(60vh,360px)] min-w-[220px] overflow-y-auto rounded-xl border border-white/10 bg-[#141416] p-1 shadow-2xl"
          sideOffset={6}
          align="start"
          collisionPadding={8}
        >
          {languages.map((lang) => (
            <DropdownMenu.Item
              key={lang.id}
              className="flex cursor-pointer select-none items-center gap-2 rounded-lg px-2.5 py-2 text-sm text-white/90 outline-none data-[highlighted]:bg-white/10"
              onPointerEnter={() => {
                if (prefetchedSlugsRef.current.has(lang.seoSlug)) return;
                prefetchedSlugsRef.current.add(lang.seoSlug);
                router.prefetch(`/${locale}/playground/${lang.seoSlug}`);
              }}
              onSelect={() => onLanguageChange(lang.id)}
            >
              {lang.id === currentLanguageId && <Check className="h-4 w-4 shrink-0 text-[#E94D1D]" />}
              {lang.id !== currentLanguageId && <span className="w-4 shrink-0" />}
              <span>{lang.name}</span>
            </DropdownMenu.Item>
          ))}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );

  const runButton = (
    <button
      type="button"
      onClick={onRun}
      disabled={!canRun || isRunning}
      title={isRunning ? undefined : (runDisabledReason ?? t("shortcutHint"))}
      aria-label={isRunning ? t("running") : (runDisabledReason ?? t("run"))}
      aria-busy={isRunning}
      className={cn(
        "inline-flex w-full max-w-[min(100%,480px)] min-w-0 shrink-0 items-center justify-center gap-2 rounded-lg px-4 text-sm font-semibold text-white shadow-lg transition",
        "min-h-[44px] md:h-10 md:w-[120px] md:min-h-0 md:max-w-none",
        "bg-gradient-to-r from-[#E94D1D] via-[#f05a28] to-[#FFAB8F] hover:brightness-110 active:scale-[0.99]",
        "disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:brightness-100",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#E94D1D]/55 focus-visible:ring-offset-2 focus-visible:ring-offset-[#0B0B0C]",
      )}
    >
      {isRunning ? (
        <>
          <Loader2 className="h-4 w-4 shrink-0 animate-spin" aria-hidden />
          <span>{t("running")}</span>
        </>
      ) : (
        <>
          <Play className="h-4 w-4 shrink-0 fill-current" aria-hidden />
          <span>{t("run")}</span>
        </>
      )}
    </button>
  );

  const settingsDropdown = (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button
          type="button"
          title={t("settings")}
          aria-label={t("settings")}
          className={cn(secondaryBtn, secondaryIconSize, "md:w-10 lg:w-10")}
        >
          <Settings className="h-4 w-4" aria-hidden />
        </button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          className="z-[200] max-h-[min(75vh,var(--radix-dropdown-menu-content-available-height))] w-[min(100vw-2rem,280px)] overflow-y-auto overscroll-contain rounded-xl border border-white/10 bg-[#141416] p-3 shadow-2xl"
          sideOffset={6}
          align="end"
          collisionPadding={8}
        >
          <EditorSettingsPanel
            editorSettings={editorSettings}
            onEditorSettingsChange={onEditorSettingsChange}
            showLanguageGuideToggle={showLanguageGuideToggle}
            runtimePanel={runtimePanel}
            onInstall={onInstall}
          />
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );

  const formatTitle =
    formatSupport === "unsupported"
      ? t("formatUnsupported", { language: current?.name ?? currentLanguageId })
      : t("formatTitle");

  const secondaryCluster = (
    <>
      {onFormat ? (
        <button
          type="button"
          onClick={onFormat}
          disabled={isFormatting}
          title={formatTitle}
          aria-label={isFormatting ? t("formatting") : t("format")}
          aria-busy={isFormatting}
          className={cn(
            secondaryBtn,
            secondaryIconSize,
            formatSupport === "unsupported" && "text-white/55",
          )}
        >
          {isFormatting ? (
            <Loader2 className="h-4 w-4 shrink-0 animate-spin" aria-hidden />
          ) : (
            <Braces
              className={cn(
                "h-4 w-4 shrink-0",
                formatSupport === "unsupported" ? "text-white/45" : "text-[#E94D1D]",
              )}
              aria-hidden
            />
          )}
          <span className="hidden max-w-[5rem] truncate xl:inline">{t("format")}</span>
        </button>
      ) : null}

      <span title={saveDisabledTooltip ?? t("saveShortcutHint")} className="inline-flex max-w-full">
        <button
          type="button"
          onClick={onSaveClick}
          disabled={saveDisabled || savePending}
          aria-label={t("save")}
          className={cn(secondaryBtn, secondaryIconSize)}
        >
          {savePending ? (
            <Loader2 className="h-4 w-4 shrink-0 animate-spin" aria-hidden />
          ) : (
            <Save className="h-4 w-4 shrink-0 text-[#E94D1D]" aria-hidden />
          )}
          <span className="hidden max-w-[5rem] truncate xl:inline">{t("save")}</span>
        </button>
      </span>

      <ShareSnippetPopover
        locale={locale}
        snippetId={savedSnippetId ?? ""}
        initialShareSlug={shareSlug}
        initialIsPasswordProtected={isPasswordProtected}
        {...(snippetEmbed ? { initialEmbed: snippetEmbed } : {})}
        disabled={!savedSnippetId || offline}
        {...(offline ? { disabledTitle: t("offlineShareDisabled") } : {})}
        triggerClassName={cn(secondaryBtn, secondaryIconSize, "xl:px-3")}
      />

      {onDownloadFile && (
        <button
          type="button"
          onClick={onDownloadFile}
          disabled={!canDownloadSource}
          title={t("downloadFileTitle")}
          aria-label={t("downloadFile")}
          className={cn(secondaryBtn, secondaryIconSize)}
        >
          <Download className="h-4 w-4 shrink-0 text-[#E94D1D]" aria-hidden />
          <span className="hidden xl:inline">{t("downloadFile")}</span>
        </button>
      )}

      {settingsDropdown}
    </>
  );

  return (
    <div className="flex w-full min-w-0 flex-col gap-2">
      <PlaygroundUpgradeModal
        open={upgradeModalOpen}
        onClose={() => setUpgradeModalOpen(false)}
        locale={locale}
      />
      {/*
        The right column is sized to its content, not to a symmetric `1fr`.
        With two equal `1fr` tracks the action cluster overflowed its own track
        and, being end-aligned, spilled left across the Run button: on an
        internal account at 1280px the tier badge sat on top of Run (measured
        2026-09-04, cluster 583px inside a 548px track). Sizing the column to
        content and letting the left cluster absorb the squeeze (it already
        truncates) makes the collision impossible instead of unlikely.
      */}
      <div className="grid w-full min-w-0 grid-cols-1 gap-2 md:grid-cols-[minmax(0,1fr)_auto_auto] md:items-center md:gap-x-3 lg:gap-x-4">
        <div className="flex min-w-0 items-center justify-between gap-2 md:col-start-1 md:row-start-1 md:justify-self-start">
          <div className="flex min-w-0 items-center gap-2">
            <PlaygroundIdeWordmark />
            {languageSelect}
            {isAuthenticated ? (
              <PlaygroundSnippetsQuickAccess
                locale={locale}
                activeSnippetId={savedSnippetId}
                onSelectSnippet={onSnippetSelect}
                onSaveClick={onSaveClick}
              />
            ) : null}
            {isAuthenticated ? historyPanel : null}
          </div>
          <div className="shrink-0 md:hidden">
            <ToolbarOverflowMenu
              locale={locale}
              savedSnippetId={savedSnippetId}
              shareSlug={shareSlug}
              isPasswordProtected={isPasswordProtected}
              {...(snippetEmbed ? { snippetEmbed } : {})}
              onSaveClick={onSaveClick}
              savePending={savePending}
              saveDisabled={saveDisabled}
              saveDisabledTooltip={saveDisabledTooltip}
              onFormat={onFormat}
              isFormatting={isFormatting}
              formatTitle={formatTitle}
              onDownloadFile={onDownloadFile}
              canDownloadSource={canDownloadSource}
              editorSettings={editorSettings}
              onEditorSettingsChange={onEditorSettingsChange}
              showLanguageGuideToggle={showLanguageGuideToggle}
              runtimePanel={runtimePanel}
              onInstall={onInstall}
              offline={offline}
              renderFooter={(closeMenu) => (
                <PlaygroundToolbarAccountMobileFooter
                  locale={locale}
                  loginNext={nextParam}
                  onCloseOverflow={closeMenu}
                  onUpgradeClick={() => setUpgradeModalOpen(true)}
                />
              )}
            />
          </div>
        </div>

        <div className="flex w-full min-w-0 justify-center md:col-start-2 md:row-start-1 md:w-auto md:justify-self-center">
          {runButton}
        </div>

        <div className="hidden min-w-0 items-center justify-end gap-1.5 justify-self-end md:col-start-3 md:row-start-1 md:flex md:gap-2">
          <PlaygroundToolbarAccount
            locale={locale}
            loginNext={nextParam}
            onUpgradeClick={() => setUpgradeModalOpen(true)}
          />
          {secondaryCluster}
        </div>
      </div>
      {status ? <div className="w-full min-w-0">{status}</div> : null}
    </div>
  );
}
